import {Trans} from '@lingui/macro';
import React, {Dispatch, FC, ReactNode, SetStateAction} from 'react';
import styled from 'styled-components';

import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon, IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PaletteColorsEnum} from '../../../../libs/shared/ui/src/styles/paletteModel';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {FilterButton} from '../../../components/common/filters/common/filterButton';
import {FilterLabel} from '../../../components/common/filters/common/filterLabel';
import {SimpleFilterDropdown} from '../../../components/common/filters/simpleFilterDropdown';
import {fuzzySearchBuilder} from '../../../helpers/fuzzySearchHelpers';
import {CompanyFeaturesEnum} from '../../../models/api/companyModel';
import {SettingsSearchInput} from '../../common/settingsSearchInput';
import {RuleRecipeCategoriesEnum} from './recipes';
import {DisplayableRuleRecipe} from './ruleLibraryRecipeCard';
import {RecipeCategoryStyles} from './ruleLibrarySections';

/*
 * Props.
 */

interface RuleLibrarySearchProps {
  value: RuleLibrarySearchState;
  onChange: Dispatch<SetStateAction<RuleLibrarySearchState>>;
}

export interface RuleLibrarySearchState {
  searchQuery?: string;
  searchCategories?: ReadonlyArray<RuleRecipeCategoriesEnum>;
  onlySmartRules?: boolean;
}

export type RuleLibrarySearchFilter = (
  recipes: ReadonlyArray<DisplayableRuleRecipe>,
) => ReadonlyArray<DisplayableRuleRecipe>;

/*
 * Styles.
 */

const StyledWrapper = styled.div`
  display: grid;
  grid-template-columns: 260px max-content max-content max-content;
  grid-column-gap: 8px;

  margin-bottom: 24px;
`;

const StyledButton = styled(Button)`
  margin-left: -4px;
`;

/*
 * Component.
 */

export const RuleLibrarySearch: FC<RuleLibrarySearchProps> = ({
  value: {searchQuery, searchCategories, onlySmartRules},
  onChange,
}) => {
  const theme = useUserTheme();
  const hasActiveFilter = searchQuery?.length || searchCategories?.length || onlySmartRules;

  return (
    <StyledWrapper>
      <SettingsSearchInput
        value={searchQuery}
        onChange={(value) => onChange((state) => ({...state, searchQuery: value}))}
      />
      <SimpleFilterDropdown
        title={<Trans id="rule-library-type-filter-title">Categories</Trans>}
        placeholder={
          <FilterLabel icon="gearSmall">
            <Trans id="rule-library-type-filter-placeholder">Categories</Trans>
          </FilterLabel>
        }
        items={CATEGORY_DROPDOWN_ITEMS}
        selectedItems={searchCategories ?? []}
        setSelectedItems={(categories) => onChange((state) => ({...state, searchCategories: categories}))}
        renderLabel={(item) => item.label}
        renderIcon={(item, isInsideValue) => {
          const paletteColor = RecipeCategoryStyles[item.key]?.[1] ?? PaletteColorsEnum.RED;

          return (
            <Icon
              name={isInsideValue ? item.iconFilled : item.icon}
              color={isInsideValue ? theme.palette[paletteColor].shade40 : undefined}
            />
          );
        }}
        onClear={() => onChange((state) => ({...state, searchCategories: undefined}))}
      />
      <FilterButton
        isActive={Boolean(onlySmartRules)}
        onClick={() => onChange((state) => ({...state, onlySmartRules: !state.onlySmartRules}))}
      >
        <FilterLabel
          icon={onlySmartRules ? 'sparklesSmall' : 'sparklesOutlineSmall'}
          color={onlySmartRules ? theme.palette.blue.shade40 : 'currentColor'}
        >
          <Trans id="rule-library-search-smart-rules-filter">Smart rules</Trans>
        </FilterLabel>
      </FilterButton>
      {hasActiveFilter && (
        <StyledButton variant={ButtonVariantsEnum.LINK} onClick={() => onChange({})}>
          <Trans id="rule-library-filter-clear-all">Clear all</Trans>
        </StyledButton>
      )}
    </StyledWrapper>
  );
};

/*
 * Helpers.
 */

const fuzzySearcher = fuzzySearchBuilder<DisplayableRuleRecipe>((recipe) => [
  recipe.title,
  recipe.description,
]);

export function makeRuleLibrarySearchFilter({
  searchQuery,
  searchCategories,
  onlySmartRules,
}: RuleLibrarySearchState): RuleLibrarySearchFilter | undefined {
  if (!searchQuery && !searchCategories?.length && !onlySmartRules) {
    return undefined;
  }

  const categories = new Set<RuleRecipeCategoriesEnum>(searchCategories);

  return (recipes) => {
    const recipesMatchingCategory = categories?.size
      ? recipes.filter((recipe) => recipe.categories.some((c) => categories.has(c)))
      : recipes;

    const maybeSmartRecipes = onlySmartRules
      ? recipesMatchingCategory.filter((recipe) =>
          recipe.requiredFeatures.includes(CompanyFeaturesEnum.SMART_RULES),
        )
      : recipesMatchingCategory;

    if (!searchQuery) {
      return maybeSmartRecipes;
    }

    return fuzzySearcher(maybeSmartRecipes, searchQuery);
  };
}

/*
 * Constants.
 */

interface DropdownItem {
  key: RuleRecipeCategoriesEnum;
  label: ReactNode;
  icon: IconName;
  iconFilled: IconName;
}

// TODO: Use icons from ruleBundleCategory directly.
const CATEGORY_DROPDOWN_ITEMS: ReadonlyArray<DropdownItem> = [
  {
    key: RuleRecipeCategoriesEnum.ARCHIVE,
    label: <Trans id="rule-library-category-archive">Archive</Trans>,
    icon: 'archiveSmall',
    iconFilled: 'archiveFilledSmall',
  },
  {
    key: RuleRecipeCategoriesEnum.ASSIGN,
    label: <Trans id="rule-library-category-assign">Assign</Trans>,
    icon: 'assignSmall',
    iconFilled: 'assignFilledSmall',
  },
  {
    key: RuleRecipeCategoriesEnum.COMMENT,
    label: <Trans id="rule-library-category-comment">Comment</Trans>,
    icon: 'discussionSmall',
    iconFilled: 'discussionFilledSmall',
  },
  {
    key: RuleRecipeCategoriesEnum.MOVE,
    label: <Trans id="rule-library-category-move">Move</Trans>,
    icon: 'moveRightSmall',
    iconFilled: 'moveRightFilledSmall',
  },
  {
    key: RuleRecipeCategoriesEnum.NOTIFY,
    label: <Trans id="rule-library-category-notify">Notify</Trans>,
    icon: 'subscribeSmall',
    iconFilled: 'bellSmall',
  },
  {
    key: RuleRecipeCategoriesEnum.REPLY,
    label: <Trans id="rule-library-category-reply">Reply</Trans>,
    icon: 'sendSmall',
    iconFilled: 'sendFilledSmall',
  },
  {
    key: RuleRecipeCategoriesEnum.SLA,
    label: <Trans id="rule-library-category-sla">SLA</Trans>,
    icon: 'slaSmall',
    iconFilled: 'slaFilledSmall',
  },
  {
    key: RuleRecipeCategoriesEnum.TAG,
    label: <Trans id="rule-library-category-tag">Tag</Trans>,
    icon: 'tagSmall',
    iconFilled: 'tagFilledSmall',
  },
];
